import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageContainer } from '@/components/layout/PageHeader'
import { DataTable } from '@/components/shared/DataTable/DataTable'
import {
  DataTableToolbar,
  DataTableServerPagination,
} from '@/components/shared/DataTable/DataTableToolbar'
import { Button, Badge, Avatar, Card, CardContent } from '@/components/ui'
import { useDebounce, usePagination } from '@/hooks'
import { adminApi } from '@/modules/admin/api/admin.api'
import { hrApi } from '@/modules/hr/api/hr.api'
import type { AdminUser } from '@/modules/admin/api/admin.api'
import { formatDateTime } from '@/lib/formatters'

export default function UsersPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const { page, limit, setPage, setLimit } = usePagination()

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', { page, limit, search: debouncedSearch }],
    queryFn: () => adminApi.getUsers({ page, limit, search: debouncedSearch }),
  })

  const toggleStatus = useMutation({
    mutationFn: ({ id, activate }: { id: string; activate: boolean }) =>
      activate ? adminApi.activateUser(id) : adminApi.deactivateUser(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const columns: ColumnDef<AdminUser>[] = [
    {
      accessorKey: 'name',
      header: 'Utilisateur',
      cell: ({ row }) => {
        const u = row.original
        return (
          <div className="flex items-center gap-3">
            <Avatar firstName={u.firstName} lastName={u.lastName} size="sm" />
            <div>
              <p className="font-medium text-[var(--tx)]">
                {u.firstName} {u.lastName}
              </p>
              <p className="text-xs text-[var(--tx3)]">{u.email}</p>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'roles',
      header: 'Rôles',
      cell: ({ row }) => {
        const roles =
          row.original.roles ??
          row.original.userRoles?.map((ur: any) => ur.role?.name).filter(Boolean) ??
          []
        return (
          <div className="flex flex-wrap gap-1">
            {roles.map((role: string) => (
              <Badge key={role} variant="info" size="sm">
                {role}
              </Badge>
            ))}
          </div>
        )
      },
    },
    {
      accessorKey: 'isActive',
      header: 'Statut',
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'success' : 'default'}>
          {row.original.isActive ? 'Actif' : 'Inactif'}
        </Badge>
      ),
    },
    {
      accessorKey: 'twoFactorEnabled',
      header: '2FA',
      cell: ({ row }) => (
        <Badge variant={row.original.twoFactorEnabled ? 'success' : 'warning'} size="sm">
          {row.original.twoFactorEnabled ? 'Activé' : 'Désactivé'}
        </Badge>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Créé le',
      cell: ({ row }) => (
        <span className="text-[var(--tx3)] text-xs">{formatDateTime(row.original.createdAt)}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const u = row.original
        return (
          <div className="flex items-center gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/users/${u.id}`)}>
              Voir
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggleStatus.mutate({ id: u.id, activate: !u.isActive })}
              isLoading={toggleStatus.isPending}
            >
              {u.isActive ? 'Désactiver' : 'Activer'}
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <PageContainer>
      <PageHeader
        title="Utilisateurs"
        description="Gérez les utilisateurs et leurs accès à NexusCorp ERP"
        breadcrumb={[{ label: 'Administration' }, { label: 'Utilisateurs' }]}
        actions={
          <Button onClick={() => setShowInviteModal(true)} size="sm">
            + Inviter un utilisateur
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total', value: data?.meta?.total ?? '—', color: 'text-[var(--tx)]' },
          {
            label: 'Actifs',
            value: (data?.data ?? []).filter((u: any) => u.isActive).length ?? '—',
            color: 'text-[var(--green-text)]',
          },
          {
            label: '2FA activé',
            value: (data?.data ?? []).filter((u: any) => u.twoFactorEnabled).length ?? '—',
            color: 'text-[var(--blue-d)]',
          },
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardContent className="py-4">
              <p className="text-xs text-[var(--tx3)] mb-1">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b border-[var(--bd)]">
            <DataTableToolbar
              onSearch={setSearch}
              searchPlaceholder="Rechercher par nom, email..."
              actions={
                <Button variant="outline" size="sm">
                  Exporter
                </Button>
              }
            />
          </div>

          <DataTable
            columns={columns}
            data={data?.data ?? []}
            isLoading={isLoading}
            emptyTitle="Aucun utilisateur"
            emptyDescription="Invitez votre premier utilisateur avec le bouton ci-dessus."
          />

          {data?.meta && (
            <DataTableServerPagination
              meta={data.meta}
              onPageChange={setPage}
              onLimitChange={setLimit}
              isLoading={isLoading}
            />
          )}
        </CardContent>
      </Card>

      {/* Modal invitation — TODO : implémenter */}
      {showInviteModal && <InviteUserModal onClose={() => setShowInviteModal(false)} />}
    </PageContainer>
  )
}

// ── Modal invitation simplifiée ───────────────────────────────────────────────

function InviteUserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    roleId: '',
    phone: '',
  })
  const [emp, setEmp] = useState({
    createEmployee: true,
    jobTitle: '',
    departmentId: '',
    hireDate: new Date().toISOString().split('T')[0],
    ipresId: '',
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState<{ message: string; matricule?: string } | null>(null)

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))
  const setE = (k: string, v: any) => setEmp((f) => ({ ...f, [k]: v }))

  const { data: rolesData } = useQuery({
    queryKey: ['admin-roles-list'],
    queryFn: () => adminApi.getRoles({ page: 1, limit: 100 }),
  })
  const { data: depts } = useQuery({
    queryKey: ['departments'],
    queryFn: () => hrApi.employees.departments(),
  })

  // Rôles système (SUPER_ADMIN, ADMIN) → pas de fiche employé
  const selectedRole = rolesData?.data?.find((r: any) => r.id === form.roleId)
  const isAdminRole =
    selectedRole?.isSystem && ['SUPER_ADMIN', 'ADMIN'].includes(selectedRole?.name ?? '')

  const invite = useMutation({
    mutationFn: () =>
      adminApi.inviteUser({
        ...form,
        ...(emp.createEmployee && !isAdminRole && emp.jobTitle
          ? {
              createEmployee: true,
              jobTitle: emp.jobTitle,
              departmentId: emp.departmentId || undefined,
              hireDate: emp.hireDate,
              ipresId: emp.ipresId || undefined,
            }
          : { createEmployee: false }),
      } as any),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      setSuccess({ message: res.message, matricule: res.matricule })
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? "Erreur lors de l'invitation"),
  })

  const inputCls =
    'w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)] focus:ring-1 focus:ring-[var(--brand-orange)]/20'
  const labelCls = 'block text-xs font-medium text-[var(--tx3)] mb-1.5'

  if (success)
    return (
      <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
        <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-md p-8 text-center space-y-4">
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 12,
              background: 'linear-gradient(135deg,#ECFDF5,#D1FAE5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
            }}
          >
            <span style={{ fontSize: 24 }}>✓</span>
          </div>
          <h3
            style={{
              fontFamily: '"Raleway", sans-serif',
              fontWeight: 800,
              fontSize: 18,
              color: 'var(--tx)',
            }}
          >
            Invitation envoyée !
          </h3>
          <p className="text-sm text-[var(--tx3)]">{success.message}</p>
          {success.matricule && (
            <div className="bg-[var(--blue-bg)] border border-[rgba(245,124,0,0.25)] rounded-lg px-4 py-2 text-sm text-[var(--blue-d)] font-medium">
              👤 Fiche employé créée — Matricule :{' '}
              <span className="font-mono font-bold">{success.matricule}</span>
            </div>
          )}
          <Button className="w-full mt-2" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    )

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm z-[99999] overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
      <div
        className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-2xl"
        style={{ boxShadow: '0 24px 64px rgba(13,8,32,0.18)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--bd)]">
          <div>
            <h2
              style={{
                fontFamily: '"Raleway", sans-serif',
                fontWeight: 800,
                fontSize: 17,
                color: 'var(--tx)',
                margin: 0,
              }}
            >
              Inviter un utilisateur
            </h2>
            <p className="text-xs text-[var(--tx3)] mt-0.5">
              Un lien d'activation valable 72h sera généré
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[var(--surface-2)] text-[var(--tx3)] hover:text-[var(--tx)] transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="bg-[var(--red-bg)] border border-[rgba(239,68,68,0.25)] rounded-lg px-4 py-3 text-sm text-[var(--red-text)] flex items-center gap-2">
              <span>⚠</span> {error}
            </div>
          )}

          {/* ── Section compte ── */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)] mb-3">
              Compte utilisateur
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Prénom *</label>
                <input
                  value={form.firstName}
                  onChange={(e) => set('firstName', e.target.value)}
                  className={inputCls}
                  placeholder="Amara"
                />
              </div>
              <div>
                <label className={labelCls}>Nom *</label>
                <input
                  value={form.lastName}
                  onChange={(e) => set('lastName', e.target.value)}
                  className={inputCls}
                  placeholder="Diallo"
                />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Email professionnel *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  className={inputCls}
                  placeholder="amara.diallo@nexuscorp.sn"
                />
              </div>
              <div>
                <label className={labelCls}>Téléphone</label>
                <input
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  className={inputCls}
                  placeholder="+221 77 000 00 00"
                />
              </div>
              <div>
                <label className={labelCls}>Rôle *</label>
                <select
                  value={form.roleId}
                  onChange={(e) => set('roleId', e.target.value)}
                  className={inputCls}
                >
                  <option value="">Sélectionner un rôle…</option>
                  {rolesData?.data.map((role: any) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ── Section fiche employé ── */}
          {!isAdminRole && (
            <div style={{ borderTop: '1px solid var(--bd)', paddingTop: 20 }}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)]">
                    Fiche employé RH
                  </p>
                  <p className="text-xs text-[var(--tx4)] mt-0.5">
                    Crée automatiquement un profil dans le module RH
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <div
                    onClick={() => setE('createEmployee', !emp.createEmployee)}
                    style={{
                      width: 40,
                      height: 22,
                      borderRadius: 99,
                      cursor: 'pointer',
                      transition: 'background .2s',
                      background: emp.createEmployee ? 'var(--brand-orange)' : 'var(--bd)',
                      position: 'relative',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 3,
                        left: emp.createEmployee ? 20 : 3,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: 'var(--white)',
                        transition: 'left .2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium text-[var(--tx3)]">
                    {emp.createEmployee ? 'Activé' : 'Désactivé'}
                  </span>
                </label>
              </div>

              {emp.createEmployee && (
                <div className="grid grid-cols-2 gap-3 bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4">
                  <div className="col-span-2">
                    <label className={labelCls}>Poste / Intitulé du poste *</label>
                    <input
                      value={emp.jobTitle}
                      onChange={(e) => setE('jobTitle', e.target.value)}
                      className={inputCls}
                      placeholder="ex : Développeur Senior, Responsable RH…"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Département</label>
                    <select
                      value={emp.departmentId}
                      onChange={(e) => setE('departmentId', e.target.value)}
                      className={inputCls}
                    >
                      <option value="">Sélectionner…</option>
                      {depts?.map((d: any) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                          {d.code ? ` (${d.code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Date d'embauche</label>
                    <input
                      type="date"
                      value={emp.hireDate}
                      onChange={(e) => setE('hireDate', e.target.value)}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>N° IPRES</label>
                    <input
                      value={emp.ipresId}
                      onChange={(e) => setE('ipresId', e.target.value)}
                      className={inputCls}
                      placeholder="Numéro IPRES"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-[var(--bd)]">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Annuler
          </Button>
          <Button
            className="flex-1"
            onClick={() => invite.mutate()}
            isLoading={invite.isPending}
            disabled={
              !form.email ||
              !form.firstName ||
              !form.lastName ||
              !form.roleId ||
              (emp.createEmployee && !isAdminRole && !emp.jobTitle)
            }
          >
            {emp.createEmployee && !isAdminRole
              ? '✓ Inviter + Créer fiche employé'
              : "Envoyer l'invitation"}
          </Button>
        </div>
      </div>
      </div>
    </div>
  )
}
